import { Table, Tooltip } from 'antd'
import { ColumnProps } from 'antd/es/table'
import isEmpty from 'lodash-es/isEmpty'
import React, { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof/src/nameof'
import Blur from '@/components/common/blur'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PlatformData } from '@/tests/mock-data/mock-content-distribution'

interface ContentDistributionTableProps {
  hiddenData: boolean
  platformDataList: PlatformData[]
}

const ContentDistributionTable: FunctionComponent<
  ContentDistributionTableProps
> = ({ platformDataList, hiddenData }) => {
  const { formatMessage, formatNumber } = useIntl()
  const { getCustomizedTagLocalizedName, getSubTagsWithRatioTooltip } =
    useCustomizedTag()
  const columns: ColumnProps<PlatformData>[] = [
    {
      key: 'rank',
      width: 30,
      render: (_data, _, index) => (
        <Blur activate={hiddenData && index !== 0}>
          <TableRank>{`#${index + 1}`}</TableRank>
        </Blur>
      ),
    },
    {
      dataIndex: nameof<PlatformData>((n) => n.customizedTagName),
      render: (
        customizedTagName: PlatformData['customizedTagName'],
        data,
        index,
      ): ReactNode => {
        const subTags = data.subCustomizedTags
          .map(({ customizedTagName }) =>
            getCustomizedTagLocalizedName(customizedTagName),
          )
          .join('．')

        return (
          <Blur activate={hiddenData && index !== 0}>
            <TagColumnWrapper>
              <div>
                <CustomizedTag>
                  {getCustomizedTagLocalizedName(customizedTagName)}
                </CustomizedTag>
                &nbsp;&nbsp;
                <CustomizedTagPercentage>
                  {formatNumber(data.customizedTagPostCountRatio, {
                    isPercentageDisplay: true,
                  })}
                </CustomizedTagPercentage>
              </div>
              {!isEmpty(data.subCustomizedTags) && (
                <Tooltip
                  placement='rightTop'
                  style={{ width: 'fit-content' }}
                  title={getSubTagsWithRatioTooltip(data.subCustomizedTags)}
                >
                  <SubCustomizedTag>{subTags}</SubCustomizedTag>
                </Tooltip>
              )}
            </TagColumnWrapper>
          </Blur>
        )
      },
    },
    {
      dataIndex: nameof<PlatformData>((n) => n.engagementRate),
      title: (
        <TableTitle>
          {formatMessage({
            id: 'kol:sponsored_posts_analysis_interactive_rate_average',
          })}
        </TableTitle>
      ),
      render: (
        engagementRate: PlatformData['engagementRate'],
        _data: PlatformData,
        index: number,
      ): ReactNode => {
        return (
          <Blur activate={hiddenData && index !== 0}>
            <Tooltip
              title={formatNumber(engagementRate, {
                isPercentageDisplay: true,
              })}
            >
              {formatNumber(engagementRate, {
                isPercentageDisplay: true,
              })}
            </Tooltip>
          </Blur>
        )
      },
    },
    {
      dataIndex: nameof<PlatformData>((n) => n.viewRate),
      title: (
        <TableTitle>
          {formatMessage({
            id: 'kol:sponsored_posts_analysis_view_rate_average',
          })}
        </TableTitle>
      ),
      render: (
        viewRate: PlatformData['viewRate'],
        _data: PlatformData,
        index: number,
      ): ReactNode => {
        return (
          <Blur activate={hiddenData && index !== 0}>
            <Tooltip
              title={formatNumber(viewRate, {
                isPercentageDisplay: true,
              })}
            >
              {formatNumber(viewRate, {
                isPercentageDisplay: true,
              })}
            </Tooltip>
          </Blur>
        )
      },
    },
  ]

  return (
    <StyledTable
      columns={columns}
      dataSource={platformDataList}
      pagination={false}
    />
  )
}

const TableRank = styled.div`
  font-size: 16px;
  font-weight: 400;
`

const SubCustomizedTag = styled.span`
  width: fit-content;
  overflow: hidden;
  color: ${({ theme }): string => theme.colors.text.tooltip};
  text-overflow: ellipsis;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 400;
`

const CustomizedTagPercentage = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }): string => theme.colors.brand.secondary};
`

const CustomizedTag = styled.span`
  font-size: 15px;
  font-weight: 600;
`

const TagColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 41px;
  word-break: break-word;
`

const TableTitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

const StyledTable = styled(Table)`
  min-width: 215px;

  .ant-table-cell {
    color: ${({ theme }): string => theme.colors.text.primary};
    font-weight: 600;
    padding: 5px 0 !important;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr:last-child > td {
    border: none !important;
  }
`

export default ContentDistributionTable
